import { useEffect, useMemo, useState } from "react";
import { http, API_BASE } from "@/lib/api";
import { readLocation } from "@/components/LocationPill";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";
import WeatherPulseNav from "./WeatherPulseNav";
import { CloudSun, Drop, MagnifyingGlass, MapPin, Warning, Wind } from "@phosphor-icons/react";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);
const dayName = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "short" });

/** City Weather Page — current conditions, forecast, today's journal,
 * community photo reports, local records and alerts for any place. */
export default function WeatherPlace() {
  const [place, setPlace] = useState(readLocation);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState(null);
  const [wx, setWx] = useState(null);
  const [journal, setJournal] = useState(null);
  const [records, setRecords] = useState(null);
  const [alerts, setAlerts] = useState(null);

  // place search (Nominatim)
  useEffect(() => {
    if (!q.trim() || q.trim().length < 3) { setHits(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q.trim())}&format=jsonv2&limit=5&addressdetails=1`);
        setHits(await r.json());
      } catch { setHits([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!place?.lat) return;
    let alive = true;
    const { lat, lon } = place;
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max&timezone=auto`)
      .then((r) => r.json()).then((d) => alive && setWx(d)).catch(() => {});
    http.get("/weather-pulse/journal", { params: { lat, lon, radius_km: 50 } })
      .then(({ data }) => alive && setJournal(data)).catch(() => {});
    http.get("/weather-pulse/records", { params: { lat, lon, radius_km: 100 } })
      .then(({ data }) => alive && setRecords(data)).catch(() => {});
    http.get("/weather/alerts/local", { params: { lat, lon, radius_km: 250, limit: 4 } })
      .then(({ data }) => alive && setAlerts(data.alerts || [])).catch(() => alive && setAlerts([]));
    return () => { alive = false; };
  }, [place?.lat, place?.lon]);

  const cur = wx?.current;
  const daily = wx?.daily;
  const placeName = [place?.city, place?.state, place?.country].filter(Boolean).join(", ") || "Your area";
  const intro = `Weather Pulse for ${placeName}. Current conditions, the week's forecast, today's community observations and local weather records — all in one place.`;

  const photoReports = useMemo(
    () => (journal?.entries || []).filter((e) => e.photo).slice(0, 6),
    [journal],
  );

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-place-page">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />

        {/* Place search */}
        <div className="relative max-w-xl mb-6">
          <MagnifyingGlass size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#3A3830]/60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search any city — Mumbai, Nairobi, London, São Paulo…"
            data-testid="wp-place-search"
            className="w-full rounded-full border border-black/15 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
          {hits && (
            <div className="absolute z-20 mt-1 w-full rounded-xl border border-black/10 bg-white shadow-lg overflow-hidden" data-testid="wp-place-results">
              {hits.length === 0 && <div className="px-4 py-3 text-sm text-[#3A3830]/70">No places found.</div>}
              {hits.map((h, i) => (
                <button
                  key={i}
                  data-testid={`wp-place-hit-${i}`}
                  onClick={() => {
                    setPlace({
                      lat: parseFloat(h.lat), lon: parseFloat(h.lon),
                      city: h.address?.city || h.address?.town || h.address?.village || h.name,
                      state: h.address?.state, country: h.address?.country,
                      display_name: h.display_name,
                    });
                    setQ(""); setHits(null);
                  }}
                  className="block w-full text-left px-4 py-2.5 hover:bg-black/5 border-b border-black/5 last:border-0 text-sm"
                >{h.display_name}</button>
              ))}
            </div>
          )}
        </div>

        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1 flex items-center gap-1.5">
          <MapPin size={12} weight="fill" /> {placeName}
        </div>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight" data-testid="wp-place-title">Weather — {place?.city || placeName}.</h1>
        <div className="mt-2"><ListenTranslateBar text={intro} testidPrefix="wp-place-tools" /></div>
        <div className="mt-4"><PageGuide pageKey="weather:place" testidPrefix="wp-place" /></div>

        {/* Current + forecast */}
        <div className="mt-6 grid gap-4 md:grid-cols-[1fr_1.4fr]">
          <div className="rounded-2xl bg-[#0C4A6E] text-[#F5EBDC] p-5" data-testid="wp-place-current">
            <div className="font-mono-tech text-[9px] tracking-[0.26em] uppercase text-[#E4A15C]">Current conditions</div>
            {cur ? (
              <>
                <div className="mt-3 flex items-center gap-3">
                  <CloudSun size={36} weight="fill" className="text-[#E4A15C]" />
                  <div className="font-serif-display text-4xl">{Math.round(cur.temperature_2m)}°C</div>
                </div>
                <div className="mt-3 flex gap-4 text-xs text-[#E5D8C4]/85">
                  <span className="inline-flex items-center gap-1"><Wind size={12} weight="fill" className="text-[#E4A15C]" />{Math.round(cur.wind_speed_10m)} km/h</span>
                  <span className="inline-flex items-center gap-1"><Drop size={12} weight="fill" className="text-[#E4A15C]" />{cur.relative_humidity_2m}%</span>
                </div>
              </>
            ) : <div className="mt-3 text-sm text-[#E5D8C4]/70">Loading…</div>}
          </div>

          <div className="rounded-2xl bg-white border border-black/10 p-5" data-testid="wp-place-forecast">
            <div className="font-mono-tech text-[9px] tracking-[0.26em] uppercase text-[color:var(--np-muted,#3A3830)]">7-day forecast · Open-Meteo</div>
            {daily ? (
              <div className="mt-3 grid grid-cols-7 gap-1 text-center">
                {daily.time.map((d, i) => (
                  <div key={d} data-testid={`wp-place-day-${i}`} className="rounded-lg bg-[color:var(--np-bg,#F5EBDC)]/60 px-1 py-2">
                    <div className="font-mono-tech text-[8px] tracking-[0.14em] uppercase">{dayName(d)}</div>
                    <div className="mt-1 text-sm font-medium">{Math.round(daily.temperature_2m_max[i])}°</div>
                    <div className="text-[10px] text-[#3A3830]/70">{Math.round(daily.temperature_2m_min[i])}°</div>
                    {(daily.precipitation_sum?.[i] ?? 0) > 0 && (
                      <div className="mt-0.5 text-[9px] text-sky-700">{daily.precipitation_sum[i].toFixed(0)} mm</div>
                    )}
                  </div>
                ))}
              </div>
            ) : <div className="mt-3 text-sm text-[#3A3830]/60">Loading…</div>}
          </div>
        </div>

        {/* Alerts */}
        {alerts && alerts.length > 0 && (
          <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="wp-place-alerts">
            <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-amber-800 mb-2 flex items-center gap-1.5">
              <Warning size={13} weight="fill" /> Active alerts near {place?.city || "this area"}
            </div>
            <ul className="space-y-1 text-sm text-amber-900">
              {alerts.map((a, i) => (
                <li key={a.id || i} data-testid={`wp-place-alert-${i}`}>
                  <b>{a.title}</b>{a.distance_km != null && <span className="text-amber-800/70"> · {a.distance_km} km away · {a.severity}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Today's journal + records */}
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl bg-white border border-black/10 p-5" data-testid="wp-place-journal">
            <div className="font-mono-tech text-[9px] tracking-[0.26em] uppercase text-[color:var(--np-muted,#3A3830)]">Today's community journal (50 km)</div>
            {!journal ? (
              <div className="mt-3 text-sm text-[#3A3830]/60">Loading…</div>
            ) : journal.entries.length === 0 ? (
              <p className="mt-3 text-sm text-[#3A3830]">No observations today — be the first to record this place's weather.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {journal.entries.slice(0, 8).map((e, i) => (
                  <li key={e.id} data-testid={`wp-place-journal-${i}`} className="text-sm flex items-center gap-2">
                    <span className="font-mono-tech text-[9px] text-[color:var(--np-brand,#B85F2A)]">{(String(e.observed_at || "").slice(11, 16)) || "—"}</span>
                    <span>{e.emoji} {e.condition_label}</span>
                    {e.measurements?.temperature_c != null && <span className="text-xs text-[#3A3830]/70">{e.measurements.temperature_c}°C</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-2xl bg-white border border-black/10 p-5" data-testid="wp-place-records">
            <div className="font-mono-tech text-[9px] tracking-[0.26em] uppercase text-[color:var(--np-muted,#3A3830)]">Local records (Weather Pulse observations)</div>
            {records?.window?.observations > 0 ? (
              <ul className="mt-3 space-y-1.5 text-sm">
                {records.records.hottest && <li>Hottest measured: <b>{records.records.hottest.value_c}°C</b> on {records.records.hottest.date}</li>}
                {records.records.coldest && <li>Coldest measured: <b>{records.records.coldest.value_c}°C</b> on {records.records.coldest.date}</li>}
                {records.records.wettest_report && <li>Heaviest reported rain: <b>{records.records.wettest_report.value_mm} mm</b> on {records.records.wettest_report.date}</li>}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-[#3A3830]">No measured records here yet. Observer measurements (with instruments) build this list.</p>
            )}
            <p className="mt-3 text-[11px] text-[#3A3830]/60">{records?.disclaimer}</p>
          </div>
        </div>

        {/* Photo reports */}
        {photoReports.length > 0 && (
          <div className="mt-4 rounded-2xl bg-white border border-black/10 p-5" data-testid="wp-place-photos">
            <div className="font-mono-tech text-[9px] tracking-[0.26em] uppercase text-[color:var(--np-muted,#3A3830)] mb-3">Weather photos near {place?.city || "you"}</div>
            <div className="grid grid-cols-3 gap-2">
              {photoReports.map((e, i) => (
                <img key={e.id} src={abs(e.photo)} alt={e.condition_label} loading="lazy" data-testid={`wp-place-photo-${i}`}
                     className="rounded-lg w-full aspect-square object-cover bg-black/5" />
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
